import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, LockKeyhole } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { api, asAttachments, type ComplaintDetail } from '../lib/api';
import { useAuth } from '../lib/auth';
import { statusLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { clientPersonName, companyName } from '../lib/people';
import { AttachmentList } from '../components/Attachments';
import { Badge, Card, CardHeader, Detail, ErrorState, LoadingBlock, PageHeader, statusTone } from '../components/ui';

/**
 * One complaint: what was said, whom about, where it stands and our answer,
 * with its history. Our steps are Viganium's — a complaint is about one of our
 * people, and the company answers it.
 */
export function ComplaintDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const company = companyName(account, locale);

  const { data: complaint, isLoading, error, refetch } = useQuery({
    queryKey: ['complaint', id],
    queryFn: () => api.get<ComplaintDetail>(`/complaints/${id}`),
    // Our answer shows without a reload.
    refetchInterval: 60_000,
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !complaint) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const closed = complaint.status === 'CLOSED';

  return (
    <>
      <div className="mb-4">
        <Link to="/complaints" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('complaints.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{complaint.number}</span>
            <span dir="auto">{complaint.title}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(complaint.status)}>{statusLabel(t, 'complaint', complaint.status)}</Badge>
            <span className="text-muted">{t(`complaints.state.${complaint.status}` as TranslationKey)}</span>
          </span>
        }
      />

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-5 xl:col-span-2">
          {complaint.reviewNote && (
            <Card className={closed ? 'border-success/35' : 'border-primary/30'}>
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">
                {t(closed ? 'complaints.ourAnswer' : 'complaints.ourNote')}
              </p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {complaint.reviewNote}
              </p>
            </Card>
          )}

          <Card>
            <CardHeader title={t('complaints.details')} />
            {complaint.details ? (
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {complaint.details}
              </p>
            ) : (
              <p className="text-body-sm text-muted">—</p>
            )}
            {complaint.attachments.length > 0 && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">{t('complaints.files')}</p>
                <AttachmentList attachments={asAttachments(complaint.attachments)} />
              </div>
            )}
          </Card>

          <p className="flex items-start gap-2 text-caption text-muted">
            <LockKeyhole className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('complaints.private')}
          </p>
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('complaints.about')}>
                {complaint.about ? (
                  <span className="inline-flex flex-col items-end">
                    <span>{localized(complaint.about)}</span>
                    {complaint.about.jobTitle && <span className="text-caption text-muted">{complaint.about.jobTitle}</span>}
                  </span>
                ) : (
                  <span className="text-muted">{t('complaints.formerMember')}</span>
                )}
              </Detail>
              <Detail label={t('common.status')}>{statusLabel(t, 'complaint', complaint.status)}</Detail>
              {complaint.raisedBy && (
                <Detail label={t('complaints.madeBy')}>{clientPersonName(complaint.raisedBy, company, t('common.you'))}</Detail>
              )}
              <Detail label={t('common.created')}>{formatDateTime(complaint.createdAt, locale)}</Detail>
              <Detail label={t('common.updated')}>{formatDateTime(complaint.updatedAt, locale)}</Detail>
            </dl>
          </Card>

          <Card>
            <CardHeader title={t('complaints.history')} />
            <ol className="relative">
              {complaint.events.map((event, index) => (
                <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                  <span className="relative flex flex-col items-center">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                    {index < complaint.events.length - 1 && <span className="w-px flex-1 bg-[var(--line)]" aria-hidden />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {event.byClient ? (
                        <span className="text-body-sm font-medium text-[var(--text-primary)]">
                          {/* "You made it" only for the reader's own; else who of the client did. */}
                          {!complaint.raisedBy || complaint.raisedBy.you
                            ? t('complaints.made')
                            : t('complaints.madeByName', { name: complaint.raisedBy.name ?? company })}
                        </span>
                      ) : (
                        <>
                          <Badge tone={statusTone(event.status)}>{statusLabel(t, 'complaint', event.status)}</Badge>
                          <span className="text-caption text-muted">· {t('common.viganium')}</span>
                        </>
                      )}
                    </div>
                    <p className="mt-0.5 text-caption text-faint tabular">{formatDateTime(event.at, locale)}</p>
                    {event.note && (
                      <p className="mt-1 whitespace-pre-wrap text-body-sm text-muted" dir="auto">
                        {event.note}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </>
  );
}
